import type { AttendeeAgendaMutation } from "@verity-connect/contracts";

const LEGACY_AGENDA_KEY = "wvcea:personal-agenda:v1";
const AGENDA_STORAGE_KEY = "wvcea:personal-agenda:v2";
const CLIENT_STORAGE_KEY = "wvcea:client-id:v1";

export interface PersonalAgendaStorage {
  itemIds: readonly string[];
  outbox: readonly AttendeeAgendaMutation[];
  identityId: string | null;
  revision: number;
  synchronizedAt: string | null;
}

export function readPersonalAgenda(storage: Pick<Storage, "getItem"> = localStorage): PersonalAgendaStorage {
  try {
    const serialized = storage.getItem(AGENDA_STORAGE_KEY);
    if (serialized) {
      const value = JSON.parse(serialized) as Partial<PersonalAgendaStorage>;
      return {
        itemIds: uniqueStrings(Array.isArray(value.itemIds) ? value.itemIds.filter(isString) : []),
        outbox: Array.isArray(value.outbox) ? value.outbox.filter(isMutation) : [],
        identityId: typeof value.identityId === "string" ? value.identityId : null,
        revision: typeof value.revision === "number" && value.revision >= 0 ? value.revision : 0,
        synchronizedAt: typeof value.synchronizedAt === "string" ? value.synchronizedAt : null,
      };
    }
    return { itemIds: parseSavedItemIds(storage.getItem(LEGACY_AGENDA_KEY)), outbox: [], identityId: null, revision: 0, synchronizedAt: null };
  } catch { return emptyAgenda(); }
}

export function writePersonalAgenda(value: PersonalAgendaStorage, storage: Pick<Storage, "setItem"> = localStorage): void {
  try {
    storage.setItem(AGENDA_STORAGE_KEY, JSON.stringify({ ...value, itemIds: uniqueStrings(value.itemIds), outbox: uniqueMutations(value.outbox) }));
    storage.setItem(LEGACY_AGENDA_KEY, JSON.stringify(uniqueStrings(value.itemIds)));
  } catch { /* The public guide remains usable when private browser storage is unavailable. */ }
}

export function queueAgendaChanges(current: PersonalAgendaStorage, nextItemIds: readonly string[], now = new Date()): PersonalAgendaStorage {
  const currentSet = new Set(current.itemIds);
  const next = uniqueStrings(nextItemIds);
  const nextSet = new Set(next);
  const outbox = [...current.outbox];
  for (const itemId of current.itemIds) if (!nextSet.has(itemId)) outbox.push(mutation(itemId, "remove", now));
  for (const itemId of next) if (!currentSet.has(itemId)) outbox.push(mutation(itemId, "save", now));
  return { ...current, itemIds: next, outbox: uniqueMutations(outbox) };
}

export function readClientId(storage: Pick<Storage, "getItem" | "setItem"> = localStorage): string {
  try {
    const saved = storage.getItem(CLIENT_STORAGE_KEY);
    if (saved && saved.length >= 8) return saved;
    const id = crypto.randomUUID();
    storage.setItem(CLIENT_STORAGE_KEY, id);
    return id;
  } catch { return crypto.randomUUID(); }
}

export function readSavedItemIds(storage: Pick<Storage, "getItem"> = localStorage): readonly string[] { return readPersonalAgenda(storage).itemIds; }
export function writeSavedItemIds(itemIds: readonly string[], storage: Pick<Storage, "setItem"> & Partial<Pick<Storage, "getItem">> = localStorage): void {
  const current = storage.getItem ? readPersonalAgenda(storage as Pick<Storage, "getItem">) : emptyAgenda();
  writePersonalAgenda(queueAgendaChanges(current, itemIds), storage);
}
export function parseSavedItemIds(serialized: string | null): readonly string[] { if (!serialized) return []; try { const value: unknown = JSON.parse(serialized); return Array.isArray(value) ? uniqueStrings(value.filter(isString)) : []; } catch { return []; } }
function emptyAgenda(): PersonalAgendaStorage { return { itemIds: [], outbox: [], identityId: null, revision: 0, synchronizedAt: null }; }
function mutation(itemId: string, action: "save" | "remove", now: Date): AttendeeAgendaMutation { return { id: crypto.randomUUID(), itemId, action, occurredAt: now.toISOString() }; }
function uniqueStrings(values: readonly string[]): readonly string[] { return [...new Set(values.filter((value) => value.length > 0))]; }
function uniqueMutations(values: readonly AttendeeAgendaMutation[]): readonly AttendeeAgendaMutation[] { return [...new Map(values.map((value) => [value.id, value])).values()]; }
function isString(value: unknown): value is string { return typeof value === "string" && value.length > 0; }
function isMutation(value: unknown): value is AttendeeAgendaMutation { if (!value || typeof value !== "object") return false; const candidate = value as Partial<AttendeeAgendaMutation>; return typeof candidate.id === "string" && typeof candidate.itemId === "string" && (candidate.action === "save" || candidate.action === "remove") && typeof candidate.occurredAt === "string"; }
